import { createRoute } from "@tanstack/react-router";
import { appAuthRoute } from "./index";
import { ENTITY } from "../../constants";
import { checkPermissionsAndRedirect } from "../../helpers/checkPermissions";
import {
  interestFormLoader,
  interestListLoader,
  interestViewLoader,
} from "../loaders/interest";
import { InterestBreadcrumb } from "../../breadcrumbs/interest";
import Spinner from "../../components/general/Spinner";
// import loading pages
import EntityListLoading from "../../pages/entities/loading/EntityListLoading";
import EntityViewLoading from "../../pages/entities/loading/EntityViewLoading";
import EntityEditLoading from "../../pages/entities/loading/EntityEditLoading";
import EntityCreateLoading from "../../pages/entities/loading/EntityCreateLoading";

const { INTEREST } = ENTITY;

export const interestRouteFactory = (parent = appAuthRoute) => {
  // If parent route is appAuthRoute => the route is NOT nested (ie. its directly under appAuthRoute)
  // If parent route is NOT appAuthRoute => route is nested
  const isNestedRoute = parent !== appAuthRoute;

  // Root

  const interestRoute = createRoute({
    getParentRoute: () => parent,
    path: INTEREST.type.plural,
    beforeLoad: () =>
      checkPermissionsAndRedirect([
        { feature: INTEREST.type.singular, action: "READ" },
      ]),
    staticData: {
      breadcrumb: INTEREST.label.upFirstPlural,
    },
  });

  // List interests

  const interestListRoute = createRoute({
    getParentRoute: () => interestRoute,
    path: "/",
    loader: interestListLoader,
    pendingComponent: () => <EntityListLoading entity={INTEREST} />,
  }).lazy(() => import("./interest.lazy").then((d) => d.InterestListRoute));

  // Create interest

  const interestCreateRoute = createRoute({
    getParentRoute: () => interestRoute,
    path: "create",
    beforeLoad: () =>
      checkPermissionsAndRedirect([
        { feature: INTEREST.type.singular, action: "CREATE" },
      ]),
    staticData: {
      breadcrumb: "Create new",
    },
    loader: interestFormLoader,
    pendingComponent: () => <EntityCreateLoading entity={INTEREST} />,
  }).lazy(() => import("./interest.lazy").then((d) => d.InterestCreateRoute));

  // View interest

  const interestViewRootRoute = createRoute({
    getParentRoute: () => interestRoute,
    path: "$interestId",
    staticData: {
      breadcrumb: InterestBreadcrumb,
    },
    loader: interestViewLoader,
  });

  // Edit interest

  const interestEditRoute = createRoute({
    getParentRoute: () => interestViewRootRoute,
    path: "edit",
    staticData: {
      breadcrumb: "Edit",
    },
    loader: interestFormLoader,
    pendingComponent: () => <EntityEditLoading entity={INTEREST} />,
  }).lazy(() => import("./interest.lazy").then((d) => d.InterestEditRoute));

  // View interest Layout

  const interestViewLayoutRoute = createRoute({
    getParentRoute: () => interestViewRootRoute,
    id: "interest-view-layout",
    pendingComponent: () => <EntityViewLoading entity={INTEREST} />,
  }).lazy(() =>
    import("./interest.lazy").then((d) => d.InterestViewLayoutRoute)
  );

  // View interest - General Tab

  const interestViewLayoutGeneralTabRoute = createRoute({
    getParentRoute: () => interestViewLayoutRoute,
    path: "/",
    pendingComponent: Spinner,
  }).lazy(() =>
    import("./interest.lazy").then((d) => d.InterestViewLayoutGeneralTabRoute)
  );

  // View interest - About Tab

  const interestViewLayoutAboutTabRoute = createRoute({
    getParentRoute: () => interestViewLayoutRoute,
    path: "about",
    staticData: {
      breadcrumb: "About",
    },
    pendingComponent: Spinner,
  }).lazy(() =>
    import("./interest.lazy").then((d) => d.InterestViewLayoutAboutTabRoute)
  );


  // Route tree
  interestRoute.addChildren([
    // if the route is nested within a parent route, we do not provide ListRoute
    // because the route is already nested in a TabListRoute
    ...(isNestedRoute ? [] : [interestListRoute]),
    interestCreateRoute,
    interestViewRootRoute.addChildren([
      interestEditRoute,
      interestViewLayoutRoute.addChildren([
        interestViewLayoutGeneralTabRoute,
        interestViewLayoutAboutTabRoute,
      ]),
    ]),
  ]);

  return interestRoute;
};

const interestRoute = interestRouteFactory();

export default interestRoute;
